/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    getProductName,
} from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { MessagingBlock } from '@strata/react-messaging-block';
import { PageTitle } from '@strata/react-page-title';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { IntakeStatusIndicator } from '@strata/react-status-indicators';
import { validationStrings } from '@strata/strata-ui-strings';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { IntakeCaseGridActionsMenu } from 'components/intakeCases/IntakeCaseGridActionsMenu';
import { getIntakeStatusDetails } from 'components/shared/IntakeStatusDetails';
import { LighthouseIntakeStatusIndicator } from 'components/shared/LighthouseIntakeStatusIndicator';
import { CustomTimeFrameOptions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { type IntakeCaseSummary, useGetIntakeCasesQuery } from 'store/api/intakeCaseApi';
import { useGetProductsMapQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';
import {
    clearArchivedIntakeCaseSearch,
    type SearchOption,
    SearchOptions,
    setArchivedIntakeCaseSearch,
} from 'store/intakeCases/archivedIntakeCaseSlice';

const InitialSortProperties = [{ id: 'archived_date', desc: true }];

const useColumns = (): ColumnDef<IntakeCaseSummary>[] => {
    const { data: segments } = useGetSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();
    return [
        {
            accessorKey: 'member_last_name',
            header: 'Patient',
            size: 150,
            cell: ({ getValue, row }) => (
                <PrimaryData
                    data={formatNameLastFirst(getValue(), row.original.member_first_name)}
                />
            ),
        },
        {
            accessorKey: 'member_id',
            header: 'Member ID',
            size: 120,
        },
        {
            accessorKey: 'product_service_oid',
            header: 'Product',
            size: 120,
            enableSorting: false,
            cell: ({ getValue, row }) => (
                <SecondaryData
                    data={getProductName(productsMap, getValue(), row.original.is_brand)}
                />
            ),
        },
        {
            accessorKey: 'segment_id',
            header: 'Health Plan',
            size: 150,
            enableSorting: false,
            cell: ({ getValue }) => (
                <SecondaryData
                    data={segments?.find(({ segmentId }) => segmentId === getValue())?.name}
                />
            ),
        },
        {
            accessorKey: 'prescriber_last_name',
            header: 'Prescriber',
            size: 120,
            cell: ({ getValue, row }) => (
                <SecondaryData
                    data={formatNameLastFirst(getValue(), row.original.prescriber_first_name)}
                />
            ),
        },
        {
            accessorKey: 'create_date',
            header: 'Created Date',
            size: 150,
            cell: ({ getValue }) => (
                <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
            ),
        },
        {
            accessorKey: 'archived_date',
            header: 'Archived Date',
            size: 150,
            cell: ({ getValue }) => (
                <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
            ),
        },
        {
            accessorKey: 'intake_status',
            header: 'Intake Status',
            size: 150,
            cell: ({ getValue }) => <LighthouseIntakeStatusIndicator status={getValue()} />,
        },
        {
            accessorKey: 'intake_status_details',
            header: 'Status Details',
            size: 150,
            cell: ({ getValue }) => <SecondaryData data={getIntakeStatusDetails(getValue())} />,
        },
        {
            accessorKey: 'intake_case_id',
            header: () => null,
            enableSorting: false,
            size: 48,
            cell: ({ getValue, row }) => (
                <IntakeCaseGridActionsMenu
                    intakeCase={{
                        intakeCaseId: getValue(),
                        intakeStatus: row.original.intake_status,
                        memberId: row.original.member_id,
                        memberFirstName: row.original.member_first_name,
                        memberLastName: row.original.member_last_name,
                        memberEligible: row.original.member_eligible,
                        isBrand: row.original.is_brand,
                        benefit: row.original.benefit,
                        productServiceOid: row.original.product_service_oid,
                        diagnosisCode: row.original.diagnosis_code,
                        prescriberVerified: row.original.prescriber_verified,
                        assignedUsername: row.original.assigned_username ?? '',
                    }}
                />
            ),
        },
    ];
};

export const ArchivedIntakeCasesGrid = () => {
    const dispatch = useAppDispatch();

    const { searchParams } = useAppSelector((state) => state.archivedIntakeCases);

    const { sort, page, updateGridSort, updateCurrentPage } = useGridState(
        Grids.ArchivedIntakeCases,
        InitialSortProperties,
    );

    const apiQueryParams = {
        page,
        timeframe: CustomTimeFrameOptions.ALL_TIME,
        intakeStatus: [
            IntakeStatusIndicator.Statuses.COMPLETE,
            IntakeStatusIndicator.Statuses.CANCELED,
        ],
        memberId: searchParams?.memberId,
        memberFirstName: searchParams?.memberFirstName,
        memberLastName: searchParams?.memberLastName,
        prescriberNpi: searchParams?.prescriberNpi,
        intakeCaseId: searchParams?.intakeCaseId,
        sort,
    };
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetIntakeCasesQuery(
        { ...apiQueryParams, page: 0 },
        { skip: !searchParams },
    );
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } = useGetIntakeCasesQuery(
        apiQueryParams,
        { skip: !searchParams },
    );

    const columns = useColumns();
    const navigate = useNavigate();

    return (
        <FormPageContainer>
            <Formik
                initialValues={searchParams ?? { searchOption: SearchOptions.Member }}
                validationSchema={Yup.object().shape(
                    {
                        searchOption: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        memberId: Yup.string().when(
                            ['searchOption', 'memberFirstName', 'memberLastName'],
                            {
                                is: (
                                    searchOption: SearchOption,
                                    memberFirstName?: string,
                                    memberLastName?: string,
                                ) =>
                                    searchOption === SearchOptions.Member &&
                                    !(memberFirstName ?? memberLastName),
                                then: (schema) =>
                                    schema.required(validationStrings.requiredField.errorMessage),
                            },
                        ),
                        memberFirstName: Yup.string(),
                        memberLastName: Yup.string().when(['searchOption', 'memberId'], {
                            is: (searchOption: SearchOption, memberId?: string) =>
                                searchOption === SearchOptions.Member && !memberId,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        prescriberNpi: Yup.string()
                            .length(10, validationStrings.exactCharacter.lambda(10))
                            .when('searchOption', {
                                is: SearchOptions.Prescriber,
                                then: (schema) =>
                                    schema.required(validationStrings.requiredField.errorMessage),
                            }),
                        intakeCaseId: Yup.string().when('searchOption', {
                            is: SearchOptions.IntakeCase,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                    },
                    [['memberId', 'memberLastName']],
                )}
                onSubmit={(values) => {
                    dispatch(setArchivedIntakeCaseSearch({ searchParams: values }));
                }}
                enableReinitialize
                validateOnMount
            >
                {(formik) => (
                    <div
                        className="flex__gap--medium margin__around--medium"
                        style={{ flexDirection: 'column', height: '100%' }}
                    >
                        <PageTitle title="Archived Intake Cases" />
                        <FormSection
                            sectionTitle="Search"
                            content={
                                <Form>
                                    <RadioButtonGroupFormik
                                        name="searchOption"
                                        label="Search By"
                                        required
                                        inlineItems
                                        options={Object.values(SearchOptions)}
                                        onChange={(value) => {
                                            (() =>
                                                formik.setValues({
                                                    searchOption: value as SearchOption,
                                                }))();
                                        }}
                                    />
                                    <div className="flex__gap--medium">
                                        {(() => {
                                            switch (formik.values.searchOption) {
                                                case SearchOptions.Member:
                                                    return (
                                                        <>
                                                            <TextInputFormik
                                                                id="memberId"
                                                                label="Member ID"
                                                                required
                                                                onChange={(e) => {
                                                                    (() =>
                                                                        formik.setValues({
                                                                            ...formik.values,
                                                                            memberId:
                                                                                e.target.value,
                                                                            memberFirstName:
                                                                                undefined,
                                                                            memberLastName:
                                                                                undefined,
                                                                        }))();
                                                                }}
                                                            />
                                                            <span className="margin__top--large">
                                                                or
                                                            </span>
                                                            <TextInputFormik
                                                                id="memberFirstName"
                                                                label="Patient First Name"
                                                                required
                                                                onChange={(e) => {
                                                                    (() =>
                                                                        formik.setValues({
                                                                            ...formik.values,
                                                                            memberFirstName:
                                                                                e.target.value,
                                                                            memberId: undefined,
                                                                        }))();
                                                                }}
                                                            />
                                                            <TextInputFormik
                                                                id="memberLastName"
                                                                label="Patient Last Name"
                                                                required
                                                                onChange={(e) => {
                                                                    (() =>
                                                                        formik.setValues({
                                                                            ...formik.values,
                                                                            memberLastName:
                                                                                e.target.value,
                                                                            memberId: undefined,
                                                                        }))();
                                                                }}
                                                            />
                                                        </>
                                                    );
                                                case SearchOptions.Prescriber:
                                                    return (
                                                        <TextInputFormik
                                                            id="prescriberNpi"
                                                            label="Prescriber NPI"
                                                            required
                                                        />
                                                    );
                                                case SearchOptions.IntakeCase:
                                                    return (
                                                        <TextInputFormik
                                                            id="intakeCaseId"
                                                            label="Intake Case ID"
                                                            required
                                                        />
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })()}

                                        <div
                                            className="flex__gap--medium"
                                            style={{
                                                flexGrow: 1,
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                type="submit"
                                                id="search-archived-intake-cases-button"
                                                label="Search"
                                                disabled={
                                                    !(formik.dirty && formik.isValid) ||
                                                    formik.isSubmitting
                                                }
                                                intent={Button.Intent.PRIMARY}
                                            />
                                            <ActionButtonText
                                                style={{ height: 30 }}
                                                id="clear-archived-intake-cases-button"
                                                label="Clear Search"
                                                onClick={() =>
                                                    dispatch(clearArchivedIntakeCaseSearch())
                                                }
                                            />
                                        </div>
                                    </div>
                                </Form>
                            }
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                maxHeight: 'calc(100% - 270px)',
                            }}
                        >
                            <Grid
                                data={searchParams && currentPageData?.intakeCases}
                                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                                columns={columns}
                                manualSorting
                                sortProperties={sort}
                                onSort={updateGridSort}
                                currentPage={searchParams && page}
                                onPageChange={updateCurrentPage}
                                totalCount={firstPageData?.count}
                                noResults={
                                    <div className="sds_no-results">
                                        <MessagingBlock
                                            size={MessagingBlock.Sizes.MEDIUM}
                                            icon={IconNames.SEARCH}
                                            primaryText="No Information to show"
                                            secondaryText="Use the search above to find an intake case."
                                        />
                                    </div>
                                }
                                onRowClick={(row: IntakeCaseSummary) => {
                                    navigate(AppRoutes.intakeCaseDetails(row.intake_case_id));
                                }}
                            />
                        </div>
                    </div>
                )}
            </Formik>
        </FormPageContainer>
    );
};
